import { toast } from 'sonner'

import {
  useChannelSettings,
  useUpdateChannelSettings,
  type ChannelSettings,
} from '@/api/endpoints/channels'
import type { ChannelDestinations } from '@/api/endpoints/destinations'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { Spinner } from '@/components/ui/Spinner'
import { errorMessage } from '@/lib/errors'
import { brandSuggestion, brandSuggestionText } from './model'

interface VideoBrandSuggestionProps {
  slug: string
  /** O @ do canal como a lista de canais o traz — vale quando os settings ainda não chegaram. */
  channelTag?: string | null | undefined
  destinations: ChannelDestinations | null | undefined
  className?: string | undefined
}

/**
 * Aviso com ação de um clique: quando o vídeo grava o @ do YouTube e o perfil
 * de outra plataforma conectada tem outro @, troca a "Marca no vídeo" (PR #308)
 * para o nome do canal. Some sozinho quando a troca é gravada, e não aparece
 * com o backend anterior ao campo.
 */
export function VideoBrandSuggestion({ slug, channelTag, destinations, className }: VideoBrandSuggestionProps) {
  const settings = useChannelSettings(slug)
  const update = useUpdateChannelSettings()

  // O tipo diz que o campo sempre vem; o backend anterior à migration 0084 não o manda.
  const data = settings.data as Partial<ChannelSettings> | undefined
  const suggestion = brandSuggestion({
    videoBrandMode: data?.video_brand_mode,
    primaryHandle: data?.channel_tag || channelTag,
    accounts: (destinations?.destinations ?? []).flatMap((d) =>
      d.account ? [{ platform: d.platform, username: d.account.username }] : [],
    ),
  })
  if (!suggestion) return null

  return (
    <Callout
      className={className}
      title={`O @ gravado no vídeo não serve no ${suggestion.platformLabel}`}
      action={
        <Button
          size="sm"
          variant="secondary"
          leadingIcon={update.isPending ? <Spinner /> : undefined}
          aria-disabled={update.isPending}
          onClick={() => {
            if (update.isPending) return
            update.mutate(
              { slug, patch: { video_brand_mode: 'name' } },
              {
                onSuccess: (saved) => {
                  if (saved.video_brand_mode === 'name') {
                    toast.success('Marca no vídeo: nome do canal. Vale para os vídeos novos.')
                  } else {
                    toast.error('O servidor ainda não conhece essa opção. Tente de novo depois do deploy.')
                  }
                },
                onError: (err) => {
                  toast.error(errorMessage(err, 'Falha ao trocar a marca no vídeo.'))
                },
              },
            )
          }}
        >
          Usar o nome do canal
        </Button>
      }
    >
      <p>{brandSuggestionText(suggestion)}</p>
    </Callout>
  )
}
